/**
 * PresentationEditor — slide-by-slide editor with inline text editing,
 * theme change, AI regenerate slide, download PPTX, delete slide.
 * Route: /app/presentations/:id/edit
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Download, Sparkles, Trash2, Copy, Plus,
  RefreshCw, ChevronLeft, ChevronRight, Palette, Loader2,
  Zap, Wand2, Languages, ChevronDown as ChevD, Image as ImageIcon, Layers,
  Undo2, Redo2, History, Shapes, Table as TableIcon, Milestone, Type as TypeIcon, X,
  Circle as CircleIcon, Square as SquareIcon, ArrowRight, Star as StarIcon, Eye, EyeOff,
  Play, Pause, RotateCw, ClipboardCopy, ClipboardPaste,
  AlignLeft, AlignCenter, AlignRight, AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd,
  AlignHorizontalDistributeCenter, AlignVerticalDistributeCenter, PictureInPicture2,
} from "lucide-react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function PresentationEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pres, setPres] = useState(null);
  const [themes, setThemes] = useState([]);
  const [current, setCurrent] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState(false);
  const [showThemes, setShowThemes] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandKits, setBrandKits] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [selectedElemId, setSelectedElemId] = useState(null);
  const [selectedElemIds, setSelectedElemIds] = useState([]);  // multi-select support
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [dragOverSlide, setDragOverSlide] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  // Undo/redo history stack of pres snapshots
  const undoStack = useRef([]);      // list of past `pres` snapshots
  const redoStack = useRef([]);      // list of future `pres` snapshots (post-undo)
  const skipHistoryRef = useRef(false); // when true, don't push to history (used by undo/redo)
  const slideRef = useRef(null);
  const autoSaveRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: cfg }, { data: bk }] = await Promise.all([
          api.get(`/presentations/${id}`),
          api.get("/presentations/config"),
          api.get("/brand-kits").catch(() => ({ data: { brand_kits: [] } })),
        ]);
        setPres(data);
        setThemes(cfg.themes);
        setBrandKits(bk.brand_kits || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
        nav("/app/presentations");
      }
    })();
  }, [id, nav]);

  // Auto-save every 4s if dirty
  useEffect(() => {
    if (!dirty || !pres) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => save(), 4000);
    return () => clearTimeout(autoSaveRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, pres]);

  const theme = useMemo(() => {
    if (pres?.brand_theme?.primary) return pres.brand_theme;
    return themes.find((t) => t.id === pres?.theme_id) || themes[0] || {};
  }, [themes, pres?.theme_id, pres?.brand_theme]);
  const slide = pres?.slides?.[current];

  // ---------- history-aware update ----------
  const setPresH = useCallback((updater) => {
    setPres((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (prev && next && next !== prev && !skipHistoryRef.current) {
        undoStack.current.push(prev);
        if (undoStack.current.length > 60) undoStack.current.shift();
        redoStack.current = [];
      }
      skipHistoryRef.current = false;
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    if (!undoStack.current.length) return;
    setPres((cur) => {
      const past = undoStack.current.pop();
      if (cur) redoStack.current.push(cur);
      skipHistoryRef.current = true;
      setDirty(true);
      return past;
    });
  }, []);
  const redo = useCallback(() => {
    if (!redoStack.current.length) return;
    setPres((cur) => {
      const future = redoStack.current.pop();
      if (cur) undoStack.current.push(cur);
      skipHistoryRef.current = true;
      setDirty(true);
      return future;
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      const target = e.target;
      const inField = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      // F5 → present mode (never in field)
      if (e.key === "F5" && !inField) { e.preventDefault(); setPresentMode(true); return; }
      // Delete / Backspace
      if ((e.key === "Delete" || e.key === "Backspace") && !meta) {
        if (!inField && (selectedElemId || selectedElemIds.length)) {
          e.preventDefault();
          deleteSelected();
          return;
        }
      }
      if (!meta) return;
      if (e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (e.key === "y" || (e.key === "z" && e.shiftKey)) { e.preventDefault(); redo(); }
      else if (e.key === "c" && !inField) { e.preventDefault(); copySelected(); }
      else if (e.key === "v" && !inField) { e.preventDefault(); pasteFromClipboard(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo, selectedElemId, selectedElemIds, slide]);

  const patchSlide = (patch) => {
    setPresH((p) => {
      const slides = [...(p.slides || [])];
      slides[current] = { ...slides[current], ...patch };
      return { ...p, slides };
    });
    setDirty(true);
  };
  const patchContent = (idx, value) => {
    const content = [...(slide.content || [])];
    content[idx] = value;
    patchSlide({ content });
  };
  const addBullet = () => patchSlide({ content: [...(slide.content || []), "New point"] });
  const removeBullet = (i) => patchSlide({ content: (slide.content || []).filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/presentations/${id}`, {
        title: pres.title, subtitle: pres.subtitle,
        theme_id: pres.theme_id, slides: pres.slides,
      });
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/presentations/${id}/export/pptx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(pres.title || "presentation").replace(/[^a-z0-9-]+/gi, "-")}.pptx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Downloaded PPTX");
    } catch (e) { toast.error("Export failed"); }
  };

  const regenerateSlide = async () => {
    const instruction = window.prompt("Instruction for the AI:", "Rewrite this slide with clearer, punchier content") || "Rewrite this slide";
    setRegen(true);
    try {
      const { data } = await api.post(`/presentations/${id}/slides/${current + 1}/regenerate`, { instruction });
      setPres((p) => {
        const slides = [...p.slides]; slides[current] = data;
        return { ...p, slides };
      });
      toast.success("Slide regenerated");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setRegen(false); }
  };

  const addSlide = () => {
    const newSlide = {
      slide_number: (pres.slides?.length || 0) + 1, slide_type: "content",
      layout: "title_and_content", title: "New slide", subtitle: null,
      content: ["Point 1", "Point 2"], visual_type: "none",
      speaker_notes: "", elements: [],
    };
    setPresH((p) => ({ ...p, slides: [...(p.slides || []), newSlide] }));
    setDirty(true);
    setCurrent(pres.slides.length);
  };
  const deleteSlide = () => {
    if (pres.slides.length <= 1) { toast.error("At least one slide required"); return; }
    if (!window.confirm("Delete this slide?")) return;
    setPresH((p) => ({ ...p, slides: p.slides.filter((_, i) => i !== current) }));
    setDirty(true);
    setCurrent(Math.max(0, current - 1));
  };
  const duplicateSlide = () => {
    setPresH((p) => {
      const slides = [...p.slides];
      slides.splice(current + 1, 0, JSON.parse(JSON.stringify(slides[current])));
      return { ...p, slides };
    });
    setDirty(true);
    setCurrent(current + 1);
  };

  // ---------- AI quick actions ----------
  const aiAction = async (actionId) => {
    let target_language = null;
    if (actionId === "translate") {
      target_language = window.prompt("Target language (English, Hindi, Hinglish, ...):", "Hindi");
      if (!target_language) return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post(
        `/presentations/${id}/slides/${current + 1}/ai-action`,
        { action: actionId, target_language },
      );
      setPres((p) => {
        const slides = [...p.slides]; slides[current] = data;
        return { ...p, slides };
      });
      toast.success("Slide updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setAiBusy(false); }
  };

  // ---------- Whole-deck translate ----------
  const translateDeck = async (lang) => {
    if (!window.confirm(`Translate the entire deck to ${lang}? This overwrites all slide text.`)) return;
    setTranslateOpen(false);
    setTranslating(true);
    try {
      const { data } = await api.post(`/presentations/${id}/translate`, { target_language: lang });
      setPres((p) => ({ ...p, slides: data.slides, language: lang }));
      toast.success(`Translated to ${lang}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setTranslating(false); }
  };

  // ---------- Nano Banana image generation ----------
  const generateImage = async () => {
    const suggested = slide.image_prompt || slide.title || "";
    const customPrompt = window.prompt("Image prompt (edit to refine):", suggested);
    if (!customPrompt) return;
    setImgBusy(true);
    try {
      const { data } = await api.post(
        `/presentations/${id}/slides/${current + 1}/generate-image`,
        { prompt: customPrompt },
      );
      setPres((p) => {
        const slides = [...p.slides]; slides[current] = data.slide;
        return { ...p, slides };
      });
      toast.success("Image generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally { setImgBusy(false); }
  };

  const clearImage = async () => {
    if (!slide.image_data) return;
    if (!window.confirm("Remove the generated image from this slide?")) return;
    try {
      await api.delete(`/presentations/${id}/slides/${current + 1}/image`);
      setPres((p) => {
        const slides = [...p.slides];
        const s = { ...slides[current] }; delete s.image_data;
        slides[current] = s;
        return { ...p, slides };
      });
      toast.success("Image removed");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // ---------- Auto-cover (Nano Banana on slide 1) ----------
  const autoCover = async () => {
    if (!window.confirm("Generate an AI cover image for slide 1? (~30-45s)")) return;
    setCoverBusy(true);
    try {
      const { data } = await api.post(`/presentations/${id}/auto-cover`);
      setPres((p) => {
        const slides = [...p.slides]; slides[0] = data.slide;
        return { ...p, slides };
      });
      setCurrent(0);
      toast.success("Cover generated ✨");
    } catch (e) { toast.error(e.response?.data?.detail || "Cover generation failed"); }
    finally { setCoverBusy(false); }
  };

  // ---------- Slide reorder via drag-and-drop ----------
  const reorderSlides = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setPresH((p) => {
      const slides = [...p.slides];
      const [moved] = slides.splice(fromIdx, 1);
      slides.splice(toIdx, 0, moved);
      return { ...p, slides: slides.map((s, i) => ({ ...s, slide_number: i + 1 })) };
    });
    setDirty(true);
    // update current slide index
    if (current === fromIdx) setCurrent(toIdx);
    else if (fromIdx < current && toIdx >= current) setCurrent(current - 1);
    else if (fromIdx > current && toIdx <= current) setCurrent(current + 1);
  };

  // ---------- PNG export (current | all) ----------
  const exportPng = async (allSlides) => {
    if (!slideRef.current) return;
    try {
      if (!allSlides) {
        const url = await toPng(slideRef.current, { pixelRatio: 2, cacheBust: true });
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(pres.title || "slide").replace(/[^a-z0-9-]+/gi, "-")}-${current + 1}.png`;
        a.click();
        toast.success("PNG downloaded");
        return;
      }
      // All slides: iterate by switching current and capturing after each render
      const originalIdx = current;
      toast.message("Rendering all slides…");
      const captures = [];
      for (let i = 0; i < pres.slides.length; i++) {
        setCurrent(i);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 250));
        // eslint-disable-next-line no-await-in-loop
        captures.push(await toPng(slideRef.current, { pixelRatio: 2, cacheBust: true }));
      }
      setCurrent(originalIdx);
      captures.forEach((url, i) => {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(pres.title || "slide").replace(/[^a-z0-9-]+/gi, "-")}-${i + 1}.png`;
        a.click();
      });
      toast.success(`Downloaded ${captures.length} PNGs`);
    } catch (e) { toast.error("PNG export failed"); console.error(e); }
  };

  // ---------- PDF export (client-side, 16:9 landscape) ----------
  const exportPdf = async () => {
    if (!slideRef.current) return;
    const originalIdx = current;
    setDownloadOpen(false);
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: [1600, 900] });
    try {
      toast.message("Building PDF…");
      for (let i = 0; i < pres.slides.length; i++) {
        setCurrent(i);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 300));
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await toPng(slideRef.current, { pixelRatio: 2, cacheBust: true });
        if (i > 0) pdf.addPage([1600, 900], "landscape");
        pdf.addImage(dataUrl, "PNG", 0, 0, 1600, 900);
      }
      setCurrent(originalIdx);
      pdf.save(`${(pres.title || "presentation").replace(/[^a-z0-9-]+/gi, "-")}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); console.error(e); }
  };

  // ---------- Brand kits ----------
  const applyBrandKit = async (kitId) => {
    setBrandOpen(false);
    try {
      const { data } = await api.post(`/presentations/${id}/apply-brand-kit/${kitId}`);
      const kit = brandKits.find((k) => k.id === kitId);
      setPres((p) => ({
        ...p,
        brand_kit_id: kitId,
        brand_theme: data.brand_theme,
        brand_logo_data: kit?.logo_data_url || null,
        brand_company_name: kit?.company_name || null,
      }));
      toast.success(`Applied "${kit?.name || "brand kit"}"`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const removeBrandKit = async () => {
    try {
      await api.delete(`/presentations/${id}/brand-kit`);
      setPres((p) => {
        const np = { ...p };
        delete np.brand_kit_id; delete np.brand_theme;
        delete np.brand_logo_data; delete np.brand_company_name;
        return np;
      });
      toast.success("Brand kit removed");
      setBrandOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // ---------- Elements (shapes, tables, timelines, textbox) ----------
  const _uid = () => Math.random().toString(36).slice(2, 10);
  const addElement = (type) => {
    const base = { id: _uid(), type, x: 12, y: 60, w: 30, h: 20, visible: true };
    let elem = { ...base };
    if (type === "rectangle") elem = { ...base, fill: theme?.primary || "#7C3AED", stroke: "transparent" };
    else if (type === "circle") elem = { ...base, w: 20, h: 20, fill: theme?.accent || "#F59E0B", stroke: "transparent" };
    else if (type === "arrow") elem = { ...base, w: 30, h: 6, fill: theme?.primary || "#7C3AED" };
    else if (type === "star") elem = { ...base, w: 12, h: 12, fill: theme?.accent || "#F59E0B" };
    else if (type === "textbox") elem = { ...base, w: 30, h: 10, text: "New text", color: theme?.fg || "#0B0D17", size: 16, bold: false };
    else if (type === "table") elem = { ...base, w: 50, h: 25, rows: [["Header 1", "Header 2", "Header 3"], ["Cell", "Cell", "Cell"], ["Cell", "Cell", "Cell"]] };
    else if (type === "timeline") elem = { ...base, w: 80, h: 20, x: 10, steps: [
      { label: "Step 1", desc: "Kick-off" }, { label: "Step 2", desc: "Build" },
      { label: "Step 3", desc: "Launch" }, { label: "Step 4", desc: "Scale" },
    ] };
    patchSlide({ elements: [...(slide.elements || []), elem] });
    setSelectedElemId(elem.id);
    setAddOpen(false);
  };
  const patchElement = (eid, patch) => {
    patchSlide({ elements: (slide.elements || []).map((e) => e.id === eid ? { ...e, ...patch } : e) });
  };
  const patchElementsBulk = (ids, patchFn) => {
    // patchFn is (element) => partial patch (used for group move where each element gets its own delta-applied patch)
    patchSlide({ elements: (slide.elements || []).map((e) => ids.includes(e.id) ? { ...e, ...patchFn(e) } : e) });
  };
  const deleteElement = (eid) => {
    patchSlide({ elements: (slide.elements || []).filter((e) => e.id !== eid) });
    if (selectedElemId === eid) setSelectedElemId(null);
    setSelectedElemIds((ids) => ids.filter((i) => i !== eid));
  };
  const deleteSelected = () => {
    const ids = selectedElemIds.length > 0 ? selectedElemIds : (selectedElemId ? [selectedElemId] : []);
    if (!ids.length) return;
    patchSlide({ elements: (slide.elements || []).filter((e) => !ids.includes(e.id)) });
    setSelectedElemId(null); setSelectedElemIds([]);
  };
  const toggleElementSelection = (eid, additive) => {
    if (additive) {
      setSelectedElemIds((ids) => ids.includes(eid) ? ids.filter((i) => i !== eid) : [...ids, eid]);
      setSelectedElemId(eid);
    } else {
      setSelectedElemIds([eid]);
      setSelectedElemId(eid);
    }
  };
  const toggleElementVisibility = (eid) => {
    const el = (slide.elements || []).find((e) => e.id === eid);
    if (el) patchElement(eid, { visible: !el.visible });
  };

  // ---------- Rotation ----------
  const rotateSelected = (delta) => {
    const ids = selectedElemIds.length > 0 ? selectedElemIds : (selectedElemId ? [selectedElemId] : []);
    if (!ids.length) return;
    patchSlide({ elements: (slide.elements || []).map((e) => ids.includes(e.id) ? { ...e, rotation: ((e.rotation || 0) + delta) % 360 } : e) });
  };

  // ---------- Copy / Paste ----------
  const clipboardRef = useRef(null);  // list of element objects (deep-cloned, ids stripped)
  const copySelected = () => {
    const ids = selectedElemIds.length > 0 ? selectedElemIds : (selectedElemId ? [selectedElemId] : []);
    if (!ids.length) return;
    const clones = (slide.elements || []).filter((e) => ids.includes(e.id)).map((e) => {
      const { id, ...rest } = e; return JSON.parse(JSON.stringify(rest));
    });
    clipboardRef.current = clones;
    toast.success(`Copied ${clones.length} element${clones.length > 1 ? "s" : ""}`);
  };
  const pasteFromClipboard = () => {
    const items = clipboardRef.current;
    if (!items || !items.length) { toast.error("Clipboard is empty"); return; }
    const OFF = 3;  // paste with small offset so it's visible
    const fresh = items.map((it) => ({ ...it, id: _uid(),
      x: Math.min(97, (it.x || 0) + OFF),
      y: Math.min(97, (it.y || 0) + OFF),
    }));
    patchSlide({ elements: [...(slide.elements || []), ...fresh] });
    setSelectedElemIds(fresh.map((f) => f.id));
    setSelectedElemId(fresh[fresh.length - 1].id);
    toast.success(`Pasted ${fresh.length} element${fresh.length > 1 ? "s" : ""}`);
  };

  // ---------- Align / Distribute ----------
  const alignSelected = (mode) => {
    const ids = selectedElemIds.length > 0 ? selectedElemIds : [];
    if (ids.length < 2) { toast.error("Select 2+ elements to align"); return; }
    const els = (slide.elements || []).filter((e) => ids.includes(e.id));
    let target = 0;
    if (mode === "left") target = Math.min(...els.map((e) => e.x));
    else if (mode === "right") target = Math.max(...els.map((e) => e.x + e.w));
    else if (mode === "hcenter") target = els.reduce((sum, e) => sum + (e.x + e.w / 2), 0) / els.length;
    else if (mode === "top") target = Math.min(...els.map((e) => e.y));
    else if (mode === "bottom") target = Math.max(...els.map((e) => e.y + e.h));
    else if (mode === "vcenter") target = els.reduce((sum, e) => sum + (e.y + e.h / 2), 0) / els.length;
    patchSlide({
      elements: (slide.elements || []).map((e) => {
        if (!ids.includes(e.id)) return e;
        if (mode === "left")    return { ...e, x: target };
        if (mode === "right")   return { ...e, x: target - e.w };
        if (mode === "hcenter") return { ...e, x: target - e.w / 2 };
        if (mode === "top")     return { ...e, y: target };
        if (mode === "bottom")  return { ...e, y: target - e.h };
        if (mode === "vcenter") return { ...e, y: target - e.h / 2 };
        return e;
      }),
    });
  };
  const distributeSelected = (axis) => {
    const ids = selectedElemIds.length > 0 ? selectedElemIds : [];
    if (ids.length < 3) { toast.error("Select 3+ elements to distribute"); return; }
    const els = [...(slide.elements || [])].filter((e) => ids.includes(e.id));
    els.sort((a, b) => axis === "h" ? (a.x - b.x) : (a.y - b.y));
    const first = els[0], last = els[els.length - 1];
    const totalGap = axis === "h" ? (last.x - first.x) : (last.y - first.y);
    const step = totalGap / (els.length - 1);
    const byId = {};
    els.forEach((e, i) => { byId[e.id] = axis === "h" ? first.x + step * i : first.y + step * i; });
    patchSlide({
      elements: (slide.elements || []).map((e) => {
        if (!(e.id in byId)) return e;
        return axis === "h" ? { ...e, x: byId[e.id] } : { ...e, y: byId[e.id] };
      }),
    });
  };

  // ---------- Present mode ----------
  const [presentMode, setPresentMode] = useState(false);
  const [presentTimer, setPresentTimer] = useState(0);
  useEffect(() => {
    if (!presentMode) return;
    const start = Date.now();
    const t = setInterval(() => setPresentTimer(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [presentMode]);
  useEffect(() => {
    if (!presentMode) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setPresentMode(false); return; }
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); setCurrent((c) => Math.min(pres.slides.length - 1, c + 1)); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); setCurrent((c) => Math.max(0, c - 1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [presentMode, pres?.slides?.length]);

  // ---------- Version history ----------
  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get(`/presentations/${id}/versions`);
      setVersions(data.versions || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const saveVersion = async () => {
    const label = window.prompt("Version label (optional):", "");
    try {
      const { data } = await api.post(`/presentations/${id}/versions`, { label });
      setVersions((v) => [{ id: data.id, label: data.label, created_at: data.created_at, slide_count: pres.slides.length }, ...v]);
      toast.success("Version saved");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const restoreVersion = async (vid) => {
    if (!window.confirm("Restore this version? Current unsaved changes will be lost.")) return;
    try {
      await api.post(`/presentations/${id}/versions/${vid}/restore`);
      const { data } = await api.get(`/presentations/${id}`);
      setPres(data);
      undoStack.current = []; redoStack.current = [];
      setDirty(false);
      setHistoryOpen(false);
      toast.success("Restored");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const deleteVersion = async (vid) => {
    if (!window.confirm("Delete this version?")) return;
    try {
      await api.delete(`/presentations/${id}/versions/${vid}`);
      setVersions((v) => v.filter((x) => x.id !== vid));
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (!pres || !slide) return <div className="p-6 text-[#4A4D57]">Loading editor…</div>;

  return (
    <div className="space-y-3 vp-fade-up">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => { if (dirty && !window.confirm("Unsaved changes — leave anyway?")) return; nav("/app/presentations"); }} className="p-2 rounded-lg hover:bg-[#F1F3F7]" data-testid="editor-back-btn"><ArrowLeft className="w-4 h-4" /></button>
          <input
            className="vp-input font-display font-semibold text-lg py-1.5 max-w-md"
            value={pres.title || ""}
            onChange={(e) => { setPres({ ...pres, title: e.target.value }); setDirty(true); }}
            data-testid="editor-title"
          />
          <span className="text-xs text-[#747680] hidden md:inline">{pres.slides.length} slides</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={undo} disabled={!undoStack.current.length} className="vp-btn-ghost text-sm py-2 px-2 disabled:opacity-40" title="Undo (Ctrl+Z)" data-testid="undo-btn"><Undo2 className="w-4 h-4" /></button>
          <button onClick={redo} disabled={!redoStack.current.length} className="vp-btn-ghost text-sm py-2 px-2 disabled:opacity-40" title="Redo (Ctrl+Y)" data-testid="redo-btn"><Redo2 className="w-4 h-4" /></button>
          <button onClick={openHistory} className="vp-btn-ghost text-sm py-2 px-2" title="Version history" data-testid="history-btn"><History className="w-4 h-4" /></button>
          <button onClick={() => setLayersOpen((v) => !v)} className={"vp-btn-ghost text-sm py-2 px-2 " + (layersOpen ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "")} title="Layers panel" data-testid="layers-btn"><Layers className="w-4 h-4" /></button>
          <button
            onClick={() => setSnapEnabled((v) => !v)}
            className={"vp-btn-ghost text-sm py-2 px-2 " + (snapEnabled ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "")}
            title={snapEnabled ? "Snap to 5% grid — ON" : "Snap — OFF"}
            data-testid="snap-toggle"
          >
            <span className="text-[10px] font-bold">{snapEnabled ? "SNAP" : "snap"}</span>
          </button>
          <button
            onClick={autoCover}
            disabled={coverBusy}
            className="vp-btn-ghost text-sm py-2 px-2 disabled:opacity-50"
            title="AI cover for slide 1"
            data-testid="auto-cover-btn"
          >
            {coverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#7C3AED]" />}
          </button>
          <button
            onClick={() => setPresentMode(true)}
            className="vp-btn-ghost text-sm py-2 px-2"
            title="Present (F5)"
            data-testid="present-btn"
          >
            <PictureInPicture2 className="w-4 h-4" />
          </button>
          <div className="relative">
            <button onClick={() => setAddOpen((v) => !v)} className="vp-btn-ghost text-sm py-2 px-3 inline-flex items-center gap-1.5" data-testid="add-element-btn">
              <Plus className="w-4 h-4" /> Add
            </button>
            {addOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setAddOpen(false)} />
                <div className="absolute right-0 top-full mt-1 vp-card bg-white shadow-xl p-2 w-48 z-50 space-y-0.5" data-testid="add-menu">
                  <div className="vp-label px-1 pt-1">Shapes</div>
                  <button onClick={() => addElement("rectangle")} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm inline-flex items-center gap-2" data-testid="add-rect"><SquareIcon className="w-3.5 h-3.5" /> Rectangle</button>
                  <button onClick={() => addElement("circle")} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm inline-flex items-center gap-2" data-testid="add-circle"><CircleIcon className="w-3.5 h-3.5" /> Circle</button>
                  <button onClick={() => addElement("arrow")} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm inline-flex items-center gap-2" data-testid="add-arrow"><ArrowRight className="w-3.5 h-3.5" /> Arrow</button>
                  <button onClick={() => addElement("star")} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm inline-flex items-center gap-2" data-testid="add-star"><StarIcon className="w-3.5 h-3.5" /> Star</button>
                  <div className="border-t border-[#E4E6EC] my-1" />
                  <div className="vp-label px-1 pt-1">Elements</div>
                  <button onClick={() => addElement("textbox")} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm inline-flex items-center gap-2" data-testid="add-textbox"><TypeIcon className="w-3.5 h-3.5" /> Text box</button>
                  <button onClick={() => addElement("table")} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm inline-flex items-center gap-2" data-testid="add-table"><TableIcon className="w-3.5 h-3.5" /> Table (3×3)</button>
                  <button onClick={() => addElement("timeline")} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm inline-flex items-center gap-2" data-testid="add-timeline"><Milestone className="w-3.5 h-3.5" /> Timeline</button>
                </div>
              </>
            )}
          </div>
          <button onClick={() => setShowThemes((v) => !v)} className="vp-btn-ghost text-sm py-2 px-3 inline-flex items-center gap-1.5" data-testid="editor-theme-btn">
            <Palette className="w-4 h-4" /> Theme
          </button>
          <div className="relative">
            <button onClick={() => setBrandOpen((v) => !v)} className="vp-btn-ghost text-sm py-2 px-3 inline-flex items-center gap-1.5" data-testid="editor-brand-btn">
              <Layers className="w-4 h-4" /> Brand{pres.brand_kit_id ? " ✓" : ""}
            </button>
            {brandOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setBrandOpen(false)} />
                <div className="absolute right-0 top-full mt-1 vp-card bg-white shadow-xl p-2 w-64 z-50 space-y-1" data-testid="brand-menu">
                  <div className="vp-label px-1 pt-1">Apply Brand Kit</div>
                  {brandKits.length === 0 && (
                    <div className="px-2 py-3 text-xs text-[#747680]">
                      No brand kits yet. <button onClick={() => nav("/app/brand-kits")} className="text-[#7C3AED] hover:underline">Create one</button>.
                    </div>
                  )}
                  {brandKits.map((k) => (
                    <button key={k.id} onClick={() => applyBrandKit(k.id)} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm flex items-center gap-2" data-testid={`brand-kit-${k.id}`}>
                      <div className="flex gap-0.5">
                        <div className="w-3 h-3 rounded-sm" style={{ background: k.primary }} />
                        <div className="w-3 h-3 rounded-sm" style={{ background: k.secondary }} />
                        <div className="w-3 h-3 rounded-sm" style={{ background: k.accent }} />
                      </div>
                      <span className="truncate flex-1">{k.name}</span>
                      {pres.brand_kit_id === k.id && <span className="text-[10px] text-[#7C3AED]">Active</span>}
                    </button>
                  ))}
                  <div className="border-t border-[#E4E6EC] my-1" />
                  <button onClick={() => { setBrandOpen(false); nav("/app/brand-kits"); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-xs text-[#7C3AED]" data-testid="manage-brand-kits">Manage brand kits</button>
                  {pres.brand_kit_id && (
                    <button onClick={removeBrandKit} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-xs text-[#B00020]" data-testid="remove-brand-kit">Remove brand kit</button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="relative">
            <button onClick={() => setTranslateOpen((v) => !v)} disabled={translating} className="vp-btn-ghost text-sm py-2 px-3 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="editor-translate-btn">
              {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />} Translate
            </button>
            {translateOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setTranslateOpen(false)} />
                <div className="absolute right-0 top-full mt-1 vp-card bg-white shadow-xl p-2 w-48 z-50 space-y-1">
                  <div className="vp-label px-1 pt-1">Translate whole deck</div>
                  {["English", "Hindi", "Hinglish", "Marathi", "Gujarati", "Bengali", "Tamil", "Telugu", "Punjabi"].map((l) => (
                    <button key={l} onClick={() => translateDeck(l)} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm" data-testid={`translate-${l}`}>{l}</button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button onClick={save} disabled={saving || !dirty} className="vp-btn-ghost text-sm py-2 px-3 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="editor-save">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : (dirty ? "Save" : "Saved")}
          </button>
          <div className="relative">
            <button onClick={() => setDownloadOpen((v) => !v)} className="vp-btn-primary text-sm py-2 px-3 inline-flex items-center gap-1.5" data-testid="editor-download">
              <Download className="w-4 h-4" /> Download <ChevD className="w-3 h-3" />
            </button>
            {downloadOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setDownloadOpen(false)} />
                <div className="absolute right-0 top-full mt-1 vp-card bg-white shadow-xl p-2 w-52 z-50 space-y-1" data-testid="download-menu">
                  <button onClick={() => { setDownloadOpen(false); download(); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm" data-testid="dl-pptx">📊 PPTX (editable)</button>
                  <button onClick={exportPdf} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm" data-testid="dl-pdf">📄 PDF (all slides)</button>
                  <button onClick={() => { setDownloadOpen(false); exportPng(false); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm" data-testid="dl-png-current">🖼 PNG (current slide)</button>
                  <button onClick={() => { setDownloadOpen(false); exportPng(true); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm" data-testid="dl-png-all">🗂 PNG (all slides)</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showThemes && (
        <div className="vp-card p-3">
          <div className="text-xs font-semibold mb-2">Choose theme</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {themes.map((t) => (
              <button
                key={t.id}
                onClick={() => { setPres({ ...pres, theme_id: t.id }); setDirty(true); setShowThemes(false); }}
                className={"p-2 rounded-lg border text-left " + (pres.theme_id === t.id ? "border-[#7C3AED]" : "border-[#E4E6EC] hover:border-[#7C3AED]/40")}
                data-testid={`theme-${t.id}`}
              >
                <div className="h-6 rounded mb-1.5 flex overflow-hidden">
                  <div style={{ background: t.primary, width: "40%" }} />
                  <div style={{ background: t.secondary, width: "30%" }} />
                  <div style={{ background: t.accent, width: "30%" }} />
                </div>
                <div className="text-[10px] font-medium">{t.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={"grid gap-3 min-h-[520px] " + (layersOpen ? "grid-cols-[220px_1fr_200px]" : "grid-cols-[220px_1fr]")}>
        {/* Slide sidebar with real mini-thumbnails + drag-to-reorder */}
        <div className="vp-card p-1.5 space-y-1.5 max-h-[calc(100vh-160px)] overflow-y-auto">
          {pres.slides.map((s, i) => (
            <div
              key={i}
              draggable
              onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverSlide(i); }}
              onDragLeave={() => setDragOverSlide((d) => d === i ? null : d)}
              onDrop={(e) => {
                e.preventDefault();
                const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                setDragOverSlide(null);
                if (!Number.isNaN(from)) reorderSlides(from, i);
              }}
            >
              <button
                onClick={() => setCurrent(i)}
                className={"w-full text-left rounded-md flex items-stretch gap-2 p-1.5 transition " +
                  (i === current ? "bg-[#7C3AED]/10 border border-[#7C3AED]" : "hover:bg-[#F1F3F7] border border-transparent") +
                  (dragOverSlide === i ? " ring-2 ring-[#7C3AED] ring-offset-1" : "")}
                data-testid={`slide-thumb-${i + 1}`}
              >
                <div className="text-[10px] text-[#747680] font-mono w-4 shrink-0 pt-1 cursor-move" title="Drag to reorder">⋮⋮</div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="rounded overflow-hidden border border-[#E4E6EC]" style={{ pointerEvents: "none" }}>
                    <SlidePreview slide={s} theme={theme} brandLogo={pres.brand_logo_data} thumbnail />
                  </div>
                  <div className="text-[10px] font-semibold truncate leading-tight">{s.title}</div>
                </div>
              </button>
            </div>
          ))}
          <button onClick={addSlide} className="w-full py-2 text-xs text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded-md inline-flex items-center justify-center gap-1" data-testid="add-slide-btn">
            <Plus className="w-3 h-3" /> Add slide
          </button>
        </div>

        {/* Slide canvas + editor */}
        <div className="space-y-3">
          {/* Slide preview */}
          <div ref={slideRef}>
            <SlidePreview
              slide={slide} theme={theme} brandLogo={pres.brand_logo_data}
              interactive
              selectedElemId={selectedElemId}
              selectedElemIds={selectedElemIds}
              onSelectElement={(eid, additive) => additive ? toggleElementSelection(eid, true) : toggleElementSelection(eid, false)}
              onPatchElement={patchElement}
              onPatchElementsBulk={patchElementsBulk}
              snapEnabled={snapEnabled}
            />
          </div>

          {/* Element operations bar (visible when element(s) selected) */}
          {(selectedElemId || selectedElemIds.length > 0) && (
            <div className="vp-card p-2 flex items-center gap-1.5 flex-wrap" data-testid="element-ops-bar">
              <div className="text-xs font-semibold text-[#7C3AED] mr-1">
                {selectedElemIds.length > 1 ? `${selectedElemIds.length} selected:` : "Selected:"}
              </div>
              <button onClick={copySelected} className="vp-btn-ghost text-xs py-1 px-2 inline-flex items-center gap-1" title="Copy (Ctrl+C)" data-testid="copy-elem-btn"><ClipboardCopy className="w-3.5 h-3.5" /> Copy</button>
              <button onClick={pasteFromClipboard} className="vp-btn-ghost text-xs py-1 px-2 inline-flex items-center gap-1" title="Paste (Ctrl+V)" data-testid="paste-elem-btn"><ClipboardPaste className="w-3.5 h-3.5" /> Paste</button>
              <button onClick={() => rotateSelected(90)} className="vp-btn-ghost text-xs py-1 px-2 inline-flex items-center gap-1" title="Rotate 90°" data-testid="rotate-btn"><RotateCw className="w-3.5 h-3.5" /> 90°</button>
              <button onClick={() => rotateSelected(-45)} className="vp-btn-ghost text-xs py-1 px-2" title="Rotate -45°">↺45°</button>
              {selectedElemIds.length >= 2 && (
                <>
                  <div className="w-px h-4 bg-[#E4E6EC] mx-1" />
                  <button onClick={() => alignSelected("left")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align left" data-testid="align-left"><AlignLeft className="w-3.5 h-3.5" /></button>
                  <button onClick={() => alignSelected("hcenter")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align horizontal center"><AlignCenter className="w-3.5 h-3.5" /></button>
                  <button onClick={() => alignSelected("right")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align right"><AlignRight className="w-3.5 h-3.5" /></button>
                  <button onClick={() => alignSelected("top")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align top"><AlignVerticalJustifyStart className="w-3.5 h-3.5" /></button>
                  <button onClick={() => alignSelected("vcenter")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align vertical center"><AlignVerticalJustifyCenter className="w-3.5 h-3.5" /></button>
                  <button onClick={() => alignSelected("bottom")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align bottom"><AlignVerticalJustifyEnd className="w-3.5 h-3.5" /></button>
                </>
              )}
              {selectedElemIds.length >= 3 && (
                <>
                  <button onClick={() => distributeSelected("h")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Distribute horizontally"><AlignHorizontalDistributeCenter className="w-3.5 h-3.5" /></button>
                  <button onClick={() => distributeSelected("v")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Distribute vertically"><AlignVerticalDistributeCenter className="w-3.5 h-3.5" /></button>
                </>
              )}
              <button onClick={deleteSelected} className="vp-btn-ghost text-xs py-1 px-2 text-[#B00020] ml-auto inline-flex items-center gap-1" title="Delete (Del)"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          )}

          {/* AI Assistant quick-actions bar */}
          <div className="vp-card p-2 flex items-center gap-1.5 flex-wrap" data-testid="ai-actions">
            <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#7C3AED] mr-1">
              <Wand2 className="w-3.5 h-3.5" /> AI:
            </div>
            {[
              { id: "shorten", label: "Shorten" },
              { id: "expand", label: "Expand" },
              { id: "professional", label: "More pro" },
              { id: "simple", label: "Simpler" },
              { id: "add_stats", label: "+ Stats" },
              { id: "add_examples", label: "+ Example" },
              { id: "improve_headline", label: "Better title" },
              { id: "speaker_notes", label: "Rich notes" },
            ].map((a) => (
              <button
                key={a.id}
                onClick={() => aiAction(a.id)}
                disabled={aiBusy}
                className="text-xs py-1 px-2.5 rounded-full border border-[#E4E6EC] hover:border-[#7C3AED] hover:bg-[#7C3AED]/5 disabled:opacity-50"
                data-testid={`ai-${a.id}`}
              >{a.label}</button>
            ))}
            {aiBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#7C3AED]" />}
          </div>

          {/* Slide controls */}
          <div className="flex items-center justify-between vp-card p-2">
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrent(Math.max(0, current - 1))} disabled={current === 0} className="p-2 rounded hover:bg-[#F1F3F7] disabled:opacity-40" data-testid="prev-slide"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-xs text-[#747680] mx-1">{current + 1} / {pres.slides.length}</span>
              <button onClick={() => setCurrent(Math.min(pres.slides.length - 1, current + 1))} disabled={current === pres.slides.length - 1} className="p-2 rounded hover:bg-[#F1F3F7] disabled:opacity-40" data-testid="next-slide"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={regenerateSlide} disabled={regen} className="vp-btn-ghost text-xs py-1.5 px-2 inline-flex items-center gap-1 disabled:opacity-50" data-testid="regen-slide">
                {regen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                AI rewrite
              </button>
              <button onClick={duplicateSlide} className="vp-btn-ghost text-xs py-1.5 px-2" data-testid="dup-slide"><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={deleteSlide} className="vp-btn-ghost text-xs py-1.5 px-2 text-[#B00020]" data-testid="del-slide"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {/* Slide editor */}
          <div className="vp-card p-4 space-y-3">
            <div>
              <label className="vp-label">Title</label>
              <input className="vp-input" value={slide.title || ""} onChange={(e) => patchSlide({ title: e.target.value })} data-testid="slide-title" />
            </div>
            {slide.subtitle != null && (
              <div>
                <label className="vp-label">Subtitle</label>
                <input className="vp-input" value={slide.subtitle || ""} onChange={(e) => patchSlide({ subtitle: e.target.value })} data-testid="slide-subtitle" />
              </div>
            )}
            <div>
              <div className="flex items-center justify-between">
                <label className="vp-label">Bullet points</label>
                <button onClick={addBullet} className="text-xs text-[#7C3AED] hover:underline" data-testid="add-bullet">+ Add</button>
              </div>
              <div className="space-y-1.5">
                {(slide.content || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <span className="text-[#7C3AED]">•</span>
                    <input className="vp-input flex-1" value={c} onChange={(e) => patchContent(i, e.target.value)} data-testid={`bullet-${i}`} />
                    <button onClick={() => removeBullet(i)} className="p-1 text-[#B00020] hover:bg-[#F1F3F7] rounded"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="vp-label">Speaker notes</label>
              <textarea className="vp-input" rows={2} value={slide.speaker_notes || ""} onChange={(e) => patchSlide({ speaker_notes: e.target.value })} data-testid="slide-notes" />
            </div>
            {/* Image generation */}
            <div className="border-t border-[#E4E6EC] pt-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="vp-label mb-0">Slide image</label>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={generateImage}
                    disabled={imgBusy}
                    className="vp-btn-ghost text-xs py-1.5 px-2 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="generate-image-btn"
                  >
                    {imgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                    {slide.image_data ? "Regenerate" : "Generate with AI"}
                  </button>
                  {slide.image_data && (
                    <button onClick={clearImage} className="vp-btn-ghost text-xs py-1.5 px-2 text-[#B00020]" data-testid="clear-image-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <input
                className="vp-input text-xs"
                value={slide.image_prompt || ""}
                onChange={(e) => patchSlide({ image_prompt: e.target.value })}
                placeholder="Prompt for image (leave blank to use slide title)"
                data-testid="slide-image-prompt"
              />
              <div className="text-[10px] text-[#747680] mt-1">Powered by Gemini Nano Banana. Generated image auto-shows in the slide preview.</div>
            </div>
            {/* Element property inspector */}
            {selectedElemId && (() => {
              const el = (slide.elements || []).find((e) => e.id === selectedElemId);
              if (!el) return null;
              return (
                <div className="border-t border-[#E4E6EC] pt-3 space-y-2" data-testid="element-inspector">
                  <div className="flex items-center justify-between">
                    <div className="vp-label mb-0 capitalize">{el.type} — properties</div>
                    <button onClick={() => deleteElement(el.id)} className="text-xs text-[#B00020] hover:underline">Delete</button>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 text-xs">
                    <label className="col-span-1">X %</label>
                    <input type="number" className="vp-input py-1 col-span-1" value={el.x} onChange={(e) => patchElement(el.id, { x: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })} />
                    <label className="col-span-1">Y %</label>
                    <input type="number" className="vp-input py-1 col-span-1" value={el.y} onChange={(e) => patchElement(el.id, { y: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })} />
                    <label className="col-span-1">W %</label>
                    <input type="number" className="vp-input py-1 col-span-1" value={el.w} onChange={(e) => patchElement(el.id, { w: Math.max(2, Math.min(100, parseFloat(e.target.value) || 2)) })} />
                    <label className="col-span-1">H %</label>
                    <input type="number" className="vp-input py-1 col-span-1" value={el.h} onChange={(e) => patchElement(el.id, { h: Math.max(2, Math.min(100, parseFloat(e.target.value) || 2)) })} />
                  </div>
                  {(el.type === "rectangle" || el.type === "circle" || el.type === "arrow" || el.type === "star") && (
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs">Fill</label>
                      <input type="color" value={el.fill} onChange={(e) => patchElement(el.id, { fill: e.target.value })} className="w-7 h-7 rounded border border-[#E4E6EC]" />
                      <input className="vp-input py-1 text-xs flex-1" value={el.fill} onChange={(e) => patchElement(el.id, { fill: e.target.value })} />
                    </div>
                  )}
                  {el.type === "textbox" && (
                    <>
                      <textarea className="vp-input py-1 text-xs" rows={2} value={el.text} onChange={(e) => patchElement(el.id, { text: e.target.value })} />
                      <div className="flex items-center gap-1.5">
                        <input type="number" min="8" max="96" className="vp-input py-1 text-xs w-16" value={el.size} onChange={(e) => patchElement(el.id, { size: parseInt(e.target.value) || 16 })} />
                        <label className="text-xs">px</label>
                        <input type="color" value={el.color} onChange={(e) => patchElement(el.id, { color: e.target.value })} className="w-7 h-7 rounded border border-[#E4E6EC]" />
                        <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={el.bold} onChange={(e) => patchElement(el.id, { bold: e.target.checked })} /> Bold</label>
                      </div>
                    </>
                  )}
                  {el.type === "table" && (
                    <div className="space-y-1">
                      <div className="text-[10px] text-[#747680]">Edit rows (tab-separated):</div>
                      <textarea className="vp-input py-1 text-xs font-mono" rows={4}
                        value={(el.rows || []).map((r) => r.join("\t")).join("\n")}
                        onChange={(e) => patchElement(el.id, { rows: e.target.value.split("\n").map((r) => r.split("\t")) })} />
                    </div>
                  )}
                  {el.type === "timeline" && (
                    <div className="space-y-1">
                      <div className="text-[10px] text-[#747680]">One step per line: `label | description`</div>
                      <textarea className="vp-input py-1 text-xs" rows={4}
                        value={(el.steps || []).map((s) => `${s.label} | ${s.desc}`).join("\n")}
                        onChange={(e) => patchElement(el.id, { steps: e.target.value.split("\n").filter(Boolean).map((line) => {
                          const [label, ...rest] = line.split("|"); return { label: (label || "").trim(), desc: rest.join("|").trim() };
                        }) })} />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        {/* Layer panel */}
        {layersOpen && (
          <aside className="vp-card p-2 space-y-1 h-fit max-h-[calc(100vh-200px)] overflow-y-auto" style={{ width: 200 }} data-testid="layer-panel">
            <div className="vp-label px-1">Layers</div>
            <div className="text-[10px] px-2 text-[#747680] pb-1">Slide {current + 1}</div>
            {(slide.elements || []).length === 0 && (
              <div className="text-[10px] text-[#747680] px-2 py-3">No custom elements yet. Use “Add” in the toolbar.</div>
            )}
            {(slide.elements || []).map((el, i) => {
              const isSel = selectedElemId === el.id || selectedElemIds.includes(el.id);
              return (
                <div key={el.id} className={"flex items-center gap-1 px-1 py-1 rounded text-xs " + (isSel ? "bg-[#7C3AED]/10" : "hover:bg-[#F1F3F7]") }>
                  <button
                    onClick={(e) => toggleElementSelection(el.id, e.shiftKey)}
                    className="flex-1 text-left truncate inline-flex items-center gap-1"
                    data-testid={`layer-${el.id}`}
                    title="Click to select, Shift+click to multi-select"
                  >
                    {el.type === "rectangle" ? <SquareIcon className="w-3 h-3" /> :
                     el.type === "circle"    ? <CircleIcon className="w-3 h-3" /> :
                     el.type === "arrow"     ? <ArrowRight className="w-3 h-3" /> :
                     el.type === "star"      ? <StarIcon   className="w-3 h-3" /> :
                     el.type === "textbox"   ? <TypeIcon   className="w-3 h-3" /> :
                     el.type === "table"     ? <TableIcon  className="w-3 h-3" /> :
                                                <Milestone className="w-3 h-3" />}
                    <span className="truncate">{el.type} #{i + 1}</span>
                  </button>
                  <button onClick={() => toggleElementVisibility(el.id)} className="p-0.5" title="Toggle visibility">
                    {el.visible === false ? <EyeOff className="w-3 h-3 opacity-50" /> : <Eye className="w-3 h-3" />}
                  </button>
                  <button onClick={() => deleteElement(el.id)} className="p-0.5 text-[#B00020]"><X className="w-3 h-3" /></button>
                </div>
              );
            })}
            {selectedElemIds.length > 1 && (
              <div className="border-t border-[#E4E6EC] pt-1 mt-1 px-1">
                <div className="text-[10px] text-[#747680] mb-1">{selectedElemIds.length} selected</div>
                <button onClick={deleteSelected} className="text-[10px] text-[#B00020] hover:underline w-full text-left" data-testid="delete-selected">Delete all selected</button>
              </div>
            )}
          </aside>
        )}
      </div>

      {/* Present mode overlay */}
      {presentMode && (
        <PresentOverlay
          slides={pres.slides}
          current={current}
          setCurrent={setCurrent}
          theme={theme}
          brandLogo={pres.brand_logo_data}
          timer={presentTimer}
          onExit={() => setPresentMode(false)}
        />
      )}

      {/* Version history modal */}
      {historyOpen && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={() => setHistoryOpen(false)}>
          <div className="vp-card bg-white w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="history-modal">
            <div className="flex items-center justify-between p-4 border-b border-[#E4E6EC]">
              <div className="font-display font-bold text-lg">Version history</div>
              <button onClick={() => setHistoryOpen(false)} className="p-1.5 rounded hover:bg-[#F1F3F7]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-2">
              <button onClick={saveVersion} className="vp-btn-primary text-sm py-2 px-3 w-full inline-flex items-center justify-center gap-1.5" data-testid="save-version-btn">
                <Save className="w-4 h-4" /> Save current as new version
              </button>
              {versions.length === 0 && <div className="text-xs text-[#747680] py-4 text-center">No saved versions yet.</div>}
              <div className="space-y-1.5">
                {versions.map((v) => (
                  <div key={v.id} className="border border-[#E4E6EC] rounded p-2 text-xs flex items-center gap-2" data-testid={`version-${v.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{v.label}</div>
                      <div className="text-[10px] text-[#747680]">{v.slide_count} slides · {new Date(v.created_at).toLocaleString()}</div>
                    </div>
                    <button onClick={() => restoreVersion(v.id)} className="vp-btn-ghost text-xs py-1 px-2" data-testid={`restore-${v.id}`}>Restore</button>
                    <button onClick={() => deleteVersion(v.id)} className="p-1 text-[#B00020] hover:bg-[#F1F3F7] rounded"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
function SlidePreview({ slide, theme, brandLogo, thumbnail, interactive, selectedElemId, selectedElemIds, onSelectElement, onPatchElement, onPatchElementsBulk, snapEnabled }) {
  const c = theme || {};
  const isDark = c.bg && (c.bg.toLowerCase() === "#0b0d17" || c.bg === "#0f172a");
  const imgSrc = slide.image_data ? `data:image/png;base64,${slide.image_data}` : null;
  const containerRef = useRef(null);
  const scaleClass = thumbnail ? "text-[6px]" : "";
  const padding = thumbnail ? "p-2" : "p-8";
  const sources = slide.sources || [];
  return (
    <div className="vp-card p-0 overflow-hidden">
      <div
        ref={containerRef}
        className="relative w-full"
        style={{ aspectRatio: "16/9", background: c.bg || "#FFFFFF", color: c.fg || "#0B0D17" }}
        onClick={(e) => { if (interactive && e.target === e.currentTarget) onSelectElement?.(null); }}
      >
        <div className="absolute left-0 top-0 bottom-0" style={{ width: thumbnail ? 3 : 6, background: c.primary || "#0B5CFF" }} />
        {brandLogo && (
          <img src={brandLogo} alt="brand-logo" className={"absolute z-10 object-contain " + (thumbnail ? "top-1 right-1 h-3" : "top-3 right-4 h-8")} data-testid={thumbnail ? undefined : "brand-logo"} />
        )}
        <div className={"absolute inset-0 flex flex-col " + padding}>
          <div>
            <h2
              className="font-display font-black leading-tight"
              style={{ color: c.primary || "#0B5CFF", fontSize: thumbnail ? "10px" : "clamp(20px, 3.6vw, 40px)" }}
            >{slide.title}</h2>
            {slide.subtitle && !thumbnail && (
              <div className="mt-1 opacity-70 text-sm" style={{ color: c.secondary || c.fg || "#4A4D57" }}>{slide.subtitle}</div>
            )}
          </div>
          <div className={"flex-1 mt-2 grid grid-cols-1 gap-2 " + (thumbnail ? "" : "md:grid-cols-2 md:gap-4 mt-4")}>
            <div className={thumbnail ? "space-y-0.5" : "space-y-2"}>
              {(slide.content || []).slice(0, thumbnail ? 3 : 8).map((item, i) => (
                <div key={i} className="flex items-start gap-1">
                  <div className={"rounded-full mt-1 shrink-0 " + (thumbnail ? "w-1 h-1" : "w-2 h-2 mt-2")} style={{ background: c.accent || "#F59E0B" }} />
                  <div className={thumbnail ? "text-[6px] leading-tight truncate" : "text-sm md:text-base"}>{item}</div>
                </div>
              ))}
            </div>
            {slide.chart ? (
              <div className={"rounded-lg " + (thumbnail ? "p-1 text-[5px]" : "p-3 text-xs")} style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}>
                <div className="opacity-70 mb-0.5">{slide.chart.title || "Chart"}</div>
                {(slide.chart.labels || []).slice(0, thumbnail ? 3 : 8).map((l, i) => {
                  const max = Math.max(...(slide.chart.values || [1]));
                  const v = (slide.chart.values || [])[i] || 0;
                  const pct = (v / max) * 100;
                  return (
                    <div key={i} className="mb-1">
                      <div className="flex justify-between text-[8px]"><span>{l}</span><span>{v}</span></div>
                      <div className="h-1 rounded" style={{ background: isDark ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.08)" }}>
                        <div className="h-full rounded" style={{ width: `${pct}%`, background: c.primary || "#0B5CFF" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : imgSrc ? (
              <div className="rounded-lg overflow-hidden flex items-center justify-center" style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}>
                <img src={imgSrc} alt={slide.image_prompt || "slide"} className="w-full h-full object-cover" data-testid={thumbnail ? undefined : "slide-generated-image"} crossOrigin="anonymous" />
              </div>
            ) : slide.image_prompt && !thumbnail ? (
              <div className="rounded-lg p-3 flex items-center justify-center text-center text-[10px] opacity-70" style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}>
                <div>
                  <div className="font-semibold">Image slot</div>
                  <div className="mt-1">{slide.image_prompt}</div>
                </div>
              </div>
            ) : null}
          </div>
          <div className={"absolute " + (thumbnail ? "bottom-0.5 right-1 text-[6px] opacity-40" : "bottom-3 right-4 text-[10px] opacity-50")}>{slide.slide_number}</div>
          {/* Citations badges (Phase 9) */}
          {!thumbnail && sources.length > 0 && (
            <div className="absolute bottom-3 left-6 flex gap-1 flex-wrap max-w-[70%]" data-testid="citations">
              {sources.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold hover:underline"
                  style={{ background: c.primary || "#7C3AED", color: "#fff", opacity: 0.85 }}
                  title={s.url}
                  data-testid={`citation-${s.n || i+1}`}
                >[{s.n || i+1}]</a>
              ))}
            </div>
          )}
          {/* Custom elements overlay (interactive when interactive=true) */}
          {(slide.elements || []).filter((e) => e.visible !== false).map((e) => (
            <ElementRenderer
              key={e.id}
              element={e}
              theme={c}
              allElements={slide.elements || []}
              interactive={interactive && !thumbnail}
              selected={selectedElemId === e.id || (selectedElemIds || []).includes(e.id)}
              selectedIds={selectedElemIds || []}
              onSelect={(additive) => onSelectElement?.(e.id, additive)}
              onPatch={(patch) => onPatchElement?.(e.id, patch)}
              onPatchBulk={onPatchElementsBulk}
              snapEnabled={snapEnabled}
              containerRef={containerRef}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// -------- Element Renderer (shapes/tables/timelines/textbox) --------
function ElementRenderer({ element, theme, allElements, interactive, selected, selectedIds, onSelect, onPatch, onPatchBulk, snapEnabled, containerRef }) {
  const style = {
    position: "absolute",
    left: `${element.x}%`,
    top: `${element.y}%`,
    width: `${element.w}%`,
    height: `${element.h}%`,
  };

  const dragState = useRef(null);
  const snap = (v) => snapEnabled ? Math.round(v / 5) * 5 : v;  // 5% grid

  const onMouseDown = (e, mode) => {
    if (!interactive) return;
    e.preventDefault();
    e.stopPropagation();
    // On body click without shift → replace selection; with shift → toggle
    if (mode === "drag") {
      onSelect?.(e.shiftKey);
    }
    const rect = containerRef?.current?.getBoundingClientRect();
    if (!rect) return;
    // For group drag, capture original positions of ALL selected elements
    const groupIds = selectedIds && selectedIds.length > 1 ? selectedIds : [element.id];
    const groupSnapshot = (allElements || []).filter((el) => groupIds.includes(el.id))
      .reduce((acc, el) => { acc[el.id] = { x: el.x, y: el.y, w: el.w, h: el.h }; return acc; }, {});
    dragState.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origX: element.x, origY: element.y, origW: element.w, origH: element.h,
      rectW: rect.width, rectH: rect.height,
      groupIds, groupSnapshot,
    };
    const move = (ev) => {
      const s = dragState.current;
      if (!s) return;
      const dx = ((ev.clientX - s.startX) / s.rectW) * 100;
      const dy = ((ev.clientY - s.startY) / s.rectH) * 100;
      if (s.mode === "drag") {
        // Group move: apply delta to each selected element with snap
        if (s.groupIds.length > 1 && onPatchBulk) {
          onPatchBulk(s.groupIds, (el) => {
            const orig = s.groupSnapshot[el.id];
            return {
              x: Math.max(0, Math.min(100 - orig.w, snap(orig.x + dx))),
              y: Math.max(0, Math.min(100 - orig.h, snap(orig.y + dy))),
            };
          });
        } else {
          onPatch?.({
            x: Math.max(0, Math.min(100 - s.origW, snap(s.origX + dx))),
            y: Math.max(0, Math.min(100 - s.origH, snap(s.origY + dy))),
          });
        }
        return;
      }
      // Resize modes — single element only
      const patch = {};
      if (s.mode === "br") {
        patch.w = Math.max(2, Math.min(100 - s.origX, snap(s.origW + dx)));
        patch.h = Math.max(2, Math.min(100 - s.origY, snap(s.origH + dy)));
      } else if (s.mode === "tr") {
        patch.w = Math.max(2, Math.min(100 - s.origX, snap(s.origW + dx)));
        patch.y = Math.max(0, Math.min(s.origY + s.origH - 2, snap(s.origY + dy)));
        patch.h = Math.max(2, s.origH - dy);
      } else if (s.mode === "bl") {
        patch.x = Math.max(0, Math.min(s.origX + s.origW - 2, snap(s.origX + dx)));
        patch.w = Math.max(2, s.origW - dx);
        patch.h = Math.max(2, Math.min(100 - s.origY, snap(s.origH + dy)));
      } else if (s.mode === "tl") {
        patch.x = Math.max(0, Math.min(s.origX + s.origW - 2, snap(s.origX + dx)));
        patch.y = Math.max(0, Math.min(s.origY + s.origH - 2, snap(s.origY + dy)));
        patch.w = Math.max(2, s.origW - dx);
        patch.h = Math.max(2, s.origH - dy);
      }
      onPatch?.(patch);
    };
    const up = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const wrapProps = interactive ? {
    onMouseDown: (e) => onMouseDown(e, "drag"),
    style: { ...style, cursor: "move", outline: selected ? "2px solid #7C3AED" : "1px dashed transparent",
             transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
             transformOrigin: "center center" },
    onClick: (e) => { e.stopPropagation(); onSelect?.(e.shiftKey); },
    "data-testid": `element-${element.id}`,
  } : { style: { ...style, transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined, transformOrigin: "center center" } };

  const handleStyle = (pos) => ({
    position: "absolute",
    width: 10, height: 10,
    background: "#7C3AED",
    border: "2px solid #fff",
    borderRadius: 2,
    zIndex: 20,
    ...(pos === "tl" ? { top: -5, left: -5, cursor: "nwse-resize" } :
       pos === "tr" ? { top: -5, right: -5, cursor: "nesw-resize" } :
       pos === "bl" ? { bottom: -5, left: -5, cursor: "nesw-resize" } :
                      { bottom: -5, right: -5, cursor: "nwse-resize" }),
  });

  const inner = (() => {
    if (element.type === "rectangle") {
      return <div style={{ width: "100%", height: "100%", background: element.fill, border: element.stroke !== "transparent" ? `1px solid ${element.stroke}` : "none", borderRadius: 6 }} />;
    }
    if (element.type === "circle") {
      return <div style={{ width: "100%", height: "100%", background: element.fill, borderRadius: "50%" }} />;
    }
    if (element.type === "arrow") {
      return (
        <svg width="100%" height="100%" viewBox="0 0 100 20" preserveAspectRatio="none">
          <line x1="0" y1="10" x2="90" y2="10" stroke={element.fill} strokeWidth="4" />
          <polygon points="90,3 100,10 90,17" fill={element.fill} />
        </svg>
      );
    }
    if (element.type === "star") {
      return (
        <svg width="100%" height="100%" viewBox="0 0 24 24">
          <polygon points="12,2 15,9 22,9 17,14 19,22 12,17 5,22 7,14 2,9 9,9" fill={element.fill} />
        </svg>
      );
    }
    if (element.type === "textbox") {
      return (
        <div style={{ width: "100%", height: "100%", color: element.color, fontSize: `${element.size}px`, fontWeight: element.bold ? 700 : 400, overflow: "hidden" }}>
          {element.text}
        </div>
      );
    }
    if (element.type === "table") {
      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <table className="w-full h-full text-[10px]" style={{ borderCollapse: "collapse" }}>
            <tbody>
              {(element.rows || []).map((row, ri) => (
                <tr key={ri} style={{ background: ri === 0 ? (theme.primary || "#7C3AED") : "transparent",
                                     color: ri === 0 ? "#fff" : (theme.fg || "#0B0D17") }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: "3px 6px", border: `1px solid ${theme.secondary || "#E4E6EC"}` }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (element.type === "timeline") {
      const steps = element.steps || [];
      return (
        <div style={{ width: "100%", height: "100%" }} className="flex items-center">
          {steps.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-center relative">
              {i < steps.length - 1 && (
                <div style={{ position: "absolute", top: 8, left: "50%", right: "-50%", height: 2, background: theme.primary || "#7C3AED" }} />
              )}
              <div style={{ width: 16, height: 16, borderRadius: "50%", background: theme.primary || "#7C3AED", zIndex: 1 }} />
              <div className="text-[9px] font-bold mt-1 text-center" style={{ color: theme.fg || "#0B0D17" }}>{s.label}</div>
              <div className="text-[8px] opacity-70 text-center px-1">{s.desc}</div>
            </div>
          ))}
        </div>
      );
    }
    return null;
  })();

  return (
    <div {...wrapProps}>
      {inner}
      {interactive && selected && (
        <>
          <div style={handleStyle("tl")} onMouseDown={(e) => onMouseDown(e, "tl")} data-testid={`resize-tl-${element.id}`} />
          <div style={handleStyle("tr")} onMouseDown={(e) => onMouseDown(e, "tr")} />
          <div style={handleStyle("bl")} onMouseDown={(e) => onMouseDown(e, "bl")} />
          <div style={handleStyle("br")} onMouseDown={(e) => onMouseDown(e, "br")} data-testid={`resize-br-${element.id}`} />
        </>
      )}
    </div>
  );
}

// -------- Present Overlay (fullscreen with speaker notes + timer) --------
function PresentOverlay({ slides, current, setCurrent, theme, brandLogo, timer, onExit }) {
  const slide = slides[current];
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  return (
    <div className="fixed inset-0 z-[200] bg-[#0B0D17] text-white flex flex-col" data-testid="present-mode">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-3">
          <div className="text-xs uppercase tracking-widest opacity-60">Presenting</div>
          <div className="font-display font-bold text-sm truncate max-w-md">{slide?.title}</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="font-mono text-lg" data-testid="present-timer">{fmt(timer)}</div>
          <div className="text-sm opacity-70">{current + 1} / {slides.length}</div>
          <button onClick={onExit} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm inline-flex items-center gap-1.5" data-testid="present-exit">
            <X className="w-4 h-4" /> Exit (Esc)
          </button>
        </div>
      </div>
      {/* Main area — big slide + notes */}
      <div className="flex-1 grid grid-cols-[1fr_320px] gap-4 p-6 min-h-0">
        <div className="flex items-center justify-center min-h-0">
          <div className="w-full max-w-6xl">
            <SlidePreview slide={slide} theme={theme} brandLogo={brandLogo} />
          </div>
        </div>
        <div className="bg-white/5 backdrop-blur rounded-xl p-4 overflow-y-auto">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Speaker notes</div>
          <div className="text-sm leading-relaxed whitespace-pre-wrap">{slide?.speaker_notes || "No notes for this slide."}</div>
          <div className="border-t border-white/10 my-4" />
          <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Next slide</div>
          {slides[current + 1] ? (
            <div>
              <div className="font-display font-semibold text-sm">{slides[current + 1].title}</div>
              <div className="text-xs opacity-70 mt-1">{(slides[current + 1].content || []).slice(0, 2).join(" • ")}</div>
            </div>
          ) : (
            <div className="text-xs opacity-70">This is the last slide.</div>
          )}
        </div>
      </div>
      {/* Bottom nav */}
      <div className="flex items-center justify-center gap-4 py-4 border-t border-white/10 bg-black/40">
        <button
          onClick={() => setCurrent(Math.max(0, current - 1))}
          disabled={current === 0}
          className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 inline-flex items-center gap-1.5"
          data-testid="present-prev"
        ><ChevronLeft className="w-4 h-4" /> Prev</button>
        <div className="text-xs opacity-60 hidden md:block">← →  •  Space to advance  •  Esc to exit</div>
        <button
          onClick={() => setCurrent(Math.min(slides.length - 1, current + 1))}
          disabled={current === slides.length - 1}
          className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 inline-flex items-center gap-1.5"
          data-testid="present-next"
        >Next <ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

